import React, { useEffect, useCallback, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import ReactModal from 'react-modal';
import { SisuContext } from '../../Context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import CloseButtonThin from '../../Primitives/CloseButton';
import CloseIconBold from '@wix/da-ds/pkg/Icons/CloseBold';
import IconButton from '../../Button/IconButton';
import { useBodyScrollLock } from '@wix/da-ds/pkg/hooks/useBodyScrollLock';
import { PopupContext } from '@wix/da-ds/pkg/popups/PopupContext';
import { useVisualViewport } from '@wix/da-ds/pkg/hooks/useVisualViewport';
import ddt from '@wix/da-ddt';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import s from './Modal.scss';
const Modal = ({ isOpen = false, isLoading = false, onRequestClose, width, height, className, closeBtnClassName, closeBtnButtonClassName, closeBtnSize, contentClassName = className, contentLabel, disableAriaHide, withCloseButton = true, shouldCloseOnOverlayClick = true, shouldReturnFocusAfterClose, overlayStyle, overlayClassName, portalClassName, forceTheme, allowThemeSkins, bodyScrollLockOptions, children, useMobileFixedHeight })=>{
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    const style = {
        content: {
            width,
            height
        },
        overlay: overlayStyle
    };
    // Mobile fixed to viewport fixes
    const [visualViewport, setVisualViewport] = useState();
    const viewportHandler = useCallback((v)=>{
        setVisualViewport(v);
    }, []);
    useVisualViewport(viewportHandler, isMobile === true && useMobileFixedHeight === true);
    const contentStyle = {};
    // Apply fixed height for mobile if requested, helper for fixing Safari being lame
    if (isMobile && visualViewport && !height) {
        style.content.height = visualViewport.height;
        style.content.transform = `translate(${visualViewport.offsetLeft}px,${visualViewport.offsetTop}px)`;
        style.content.scale = visualViewport.scale;
        // Calculate positions from top left
        style.content.top = 0;
        style.content.left = 0;
        style.content.position = 'fixed';
        // Set the max height of the content, fixes content expanding beyond viewport
        contentStyle.maxHeight = visualViewport.height;
        ddt.log('popup', 'Adjusting to viewport offset', visualViewport);
    }
    // Mobile body scroll locking
    const contentRef = useRef(null);
    const { onLock, onUnlock, setScrollableRef, scrollableElementRef } = useBodyScrollLock(bodyScrollLockOptions);
    useEffect(()=>{
        const focusedEl = document.activeElement;
        const contentEl = contentRef.current;
        const isFocused = (el)=>el === focusedEl || el.contains(focusedEl);
        const needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
        let focusTimeout;
        if (needsFocus) {
            // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
            focusTimeout = setTimeout(()=>contentEl.focus(), 100);
        }
        return ()=>focusTimeout && clearTimeout(focusTimeout);
    }, []);
    const setContentRef = (instance)=>{
        contentRef.current = instance;
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
        }
    };
    const onClose = useCallback(()=>{
        onRequestClose && onRequestClose();
    }, [
        onRequestClose
    ]);
    return /*#__PURE__*/ React.createElement(ReactModal, {
        isOpen: isOpen,
        onAfterOpen: onLock,
        onRequestClose: onClose,
        onAfterClose: onUnlock,
        shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
        shouldReturnFocusAfterClose: shouldReturnFocusAfterClose,
        className: classnames(s['modal'], className),
        overlayClassName: classnames(s['overlay'], overlayClassName),
        portalClassName: classnames('ReactModalPortal', portalClassName),
        contentLabel: contentLabel,
        style: style,
        ariaHideApp: !disableAriaHide,
        contentRef: setContentRef
    }, /*#__PURE__*/ React.createElement(ThemeOverride, {
        className: classnames(!allowThemeSkins && 'no-theme-skin', contentClassName),
        style: contentStyle
    }, withCloseButton && /*#__PURE__*/ React.createElement(SisuContext.Consumer, null, (isSisu)=>isSisu ? /*#__PURE__*/ React.createElement(CloseButtonThin, {
            className: classnames(s['sisu-close-button'], closeBtnClassName),
            onClick: onClose
        }) : /*#__PURE__*/ React.createElement(IconButton, {
            className: classnames(s['close-button'], closeBtnButtonClassName),
            onClick: onClose,
            "aria-label": t('common.close'),
            noHoverEffect: true
        }, /*#__PURE__*/ React.createElement(CloseIconBold, {
            className: closeBtnClassName,
            size: closeBtnSize,
            disableDefaultColors: true
        }))), /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "Modal"
    }, /*#__PURE__*/ React.createElement(PopupContext.Provider, {
        value: {
            isInPopup: true,
            setScrollableContentRef: setScrollableRef
        }
    }, isLoading ? /*#__PURE__*/ React.createElement(LoadingIndicator, null) : children))));
};
Modal.displayName = 'Modal';
export default Modal;
